import React, { useEffect } from "react";
import smart from "../assets/MERCHANT.jpg";
import home from "../assets/merchant1.JPG";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styles from "../styles/Merchant.module.css";
import qrStyles from "../styles/QR.module.css";
import payment from "../assets/merchants.png";
import { useLocation, Link } from "react-router-dom";
import {
  FaGooglePlay,
  FaChartLine,
  FaReceipt,
  FaUsers,
  FaUserFriends,
  FaQrcode,
  FaFilter,
  FaFileInvoiceDollar,
  FaStore,
  FaCheckCircle,
  FaPlug,
  FaHeadset,
  FaLock,
  FaMobileAlt,
  FaCalendarAlt,
} from "react-icons/fa";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=net.arifpay.arif_merchant_app";

const introHighlights = [
  { label: "Live sales", icon: FaChartLine },
  { label: "QR payments", icon: FaQrcode },
  { label: "Event management", icon: FaCalendarAlt },
  { label: "Multi-store", icon: FaStore },
];

const features = [
  {
    title: "Sales Dashboard",
    desc: "Get a real-time view of your sales, income, and transaction history, all in one place.",
    icon: FaChartLine,
  },
  {
    title: "Digital Receipts",
    desc: "Send receipts to customers via SMS or email with just a tap.",
    icon: FaReceipt,
  },
  {
    title: "Employee Management",
    desc: "Add cashiers, assign permissions, and monitor team performance.",
    icon: FaUsers,
  },
  {
    title: "Customer Insights",
    desc: "Understand your buyers better with built-in analytics and purchase patterns.",
    icon: FaUserFriends,
  },
  {
    title: "QR Code Payment Acceptance",
    desc: "Accept payments instantly with a unique QR code generated for your store.",
    icon: FaQrcode,
  },
  {
    title: "Transaction History & Filters",
    desc: "Easily search, filter, and download transaction logs for reconciliation.",
    icon: FaFilter,
  },
  {
    title: "Settlement Reports",
    desc: "Track daily, weekly, or monthly settlements for transparent financial reporting.",
    icon: FaFileInvoiceDollar,
  },
  {
    title: "Multi-Store Management",
    desc: "Own more than one shop? Control all your locations from a single app.",
    icon: FaStore,
  },
  {
    title: "Event Management",
    desc: "Create and run events from the Merchant App — set tickets or collections, collect payments, and track attendance and sales in one place.",
    icon: FaCalendarAlt,
  },
];

const whyPoints = [
  {
    text: "Easy to set up, easier to use",
    icon: FaCheckCircle,
  },
  {
    text: "Works seamlessly with Arifpay POS and Online Payment Gateway",
    icon: FaPlug,
  },
  {
    text: "Local support team — ready to help 24/7",
    icon: FaHeadset,
  },
  {
    text: "Secured with bank-level encryption",
    icon: FaLock,
  },
];

const MerchantApp = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div>
      <Header />
      <div className={styles.equbPage}>
        <div
          className={`${qrStyles.empoweringSection} ${qrStyles.equbHeroSection}`}
        >
          <div className={qrStyles.hero}>
            <div className={qrStyles.heroContent}>
              <p className={qrStyles.msmesLabel}>Arif Merchant</p>
              <h1 className={qrStyles.heroTitle}>
                Power Your Business Payments Anytime, Anywhere
              </h1>
              <p className={qrStyles.equbHeroDesc}>
                The Arif Merchant App is your mobile-first business payments app
                for managing sales, transactions, event management, and customer
                relationships on the go. Whether you run a cafe, a boutique, an
                event, or an online store in Ethiopia, stay in control and make
                data-driven decisions in real time.
              </p>
              <Link to="/become-a-merchant">
                <button className={qrStyles.contactButton}>
                  Become Merchant →
                </button>
              </Link>
            </div>
            <div className={qrStyles.heroImage}>
              <div className={qrStyles.imageWithButtons}>
                <img
                  className={qrStyles.equbHeroImage}
                  src={home}
                  alt="Arif Merchant App"
                />
              </div>
            </div>
          </div>
        </div>

        <section className={styles.equbFeaturesIntro}>
          <div className={styles.equbFeaturesIntroCard}>
            <span className={styles.equbFeaturesIntroBadge}>KEY FEATURES</span>
            <h3 className={styles.equbIntroTitle}>Built for All Merchants</h3>
            <p className={styles.equbIntroPara}>
              With a sleek interface and intuitive tools, the Arif Merchant App
              transforms your smartphone into a business powerhouse. From daily
              transaction monitoring and event management to inventory and
              reports, it&apos;s all in your hands.
            </p>
            <div className={styles.equbFeaturesIntroPills}>
              {introHighlights.map(({ label, icon }) => {
                const HighlightIcon = icon;
                return (
                  <span key={label} className={styles.equbFeaturesIntroPill}>
                    <HighlightIcon aria-hidden />
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        </section>

        <section className={styles.equbFeaturesSection}>
          <div className={styles.equbFeaturesGrid}>
            {features.map(({ title, desc, icon }) => {
              const FeatureIcon = icon;
              return (
                <article key={title} className={styles.equbFeatureCard}>
                  <div className={styles.equbFeatureIconWrap}>
                    <FeatureIcon aria-hidden />
                  </div>
                  <div className={styles.equbFeatureCardBody}>
                    <h4 className={styles.equbFeatureCardTitle}>{title}</h4>
                    <p className={styles.equbFeatureCardDesc}>{desc}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className={styles.equbWhySection}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>ARIF MERCHANT APP</p>
            <h2 className={styles.equbSectionTitle}>Why Arif Merchant App?</h2>
          </div>
          <div className={styles.equbWhyLayout}>
            <div className={styles.equbWhyImageWrap}>
              <img
                src={payment}
                alt="Arif Merchant features"
                className={styles.equbWhyImage}
              />
            </div>
            <ul className={styles.equbWhyList}>
              {whyPoints.map(({ text, icon }) => {
                const WhyIcon = icon;
                return (
                  <li key={text} className={styles.equbWhyItem}>
                    <div className={styles.equbWhyItemIcon}>
                      <WhyIcon aria-hidden />
                    </div>
                    <span className={styles.equbWhyItemText}>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <div className={styles.equbDownloadBanner}>
          <div className={styles.equbDownloadCard}>
            <div className={styles.equbDownloadInner}>
              <div className={styles.equbDownloadImgWrap}>
                <img
                  src={smart}
                  alt="Arif Merchant on a device"
                  className={styles.equbDownloadImg}
                />
              </div>
              <div className={styles.equbGetApp}>
                <h2 className={styles.equbDownloadTitle}>Get the App Today</h2>
                <p className={styles.equbDownloadDesc}>
                  <span className={styles.equbDownloadSpark}>✦</span>{" "}
                  Take your business to the next level. Download the Arif
                  Merchant App from Google Play and enjoy hassle-free digital
                  business management.
                </p>
                <div className={styles.storeButtons}>
                  <a
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.storeButton} ${styles.storeButtonGoogle}`}
                    aria-label="Get it on Google Play"
                  >
                    <FaGooglePlay
                      className={styles.storeButtonIcon}
                      aria-hidden
                    />
                    <span className={styles.storeButtonText}>
                      <span className={styles.storeButtonSmall}>Get it on</span>
                      <span className={styles.storeButtonBig}>Google Play</span>
                    </span>
                  </a>
                  <Link
                    to="/become-a-merchant"
                    className={`${styles.storeButton} ${styles.storeButtonApple}`}
                    aria-label="Become a merchant"
                  >
                    <FaMobileAlt
                      className={styles.storeButtonIcon}
                      aria-hidden
                    />
                    <span className={styles.storeButtonText}>
                      <span className={styles.storeButtonSmall}>
                        Ready to start?
                      </span>
                      <span className={styles.storeButtonBig}>
                        Become Merchant
                      </span>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MerchantApp;
